import { MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import type { TaskSyncFreshness } from '../../hooks/useClients';
import { useTranslation } from '../../i18n';
import { formatCalendarDate } from '../../utils/calendarDate';

/**
 * Tells staff when the task counts on screen are not current.
 *
 * WHY THIS EXISTS. Supervision serves task counts from the ptc-core SQL mirror
 * (`USE_SQL_CORE=true`). That mirror's feed stopped on 2026-07-31, and on 2026-08-28 the counts
 * for 52 clients were measured wrong — 28 over-reported by 66 phantom tasks, 24 under-reported by
 * 26 invisible ones — with nothing on any screen to suggest it, because a frozen snapshot renders
 * exactly like live data.
 *
 * This does NOT fix the counts and must never be described as if it does. It makes silent-wrong
 * into visibly-stale. The durable fix is ptc-core resuming the feed; when that lands the watermark
 * moves inside the threshold and this banner stops rendering on its own. It is self-retiring by
 * construction — nothing has to be remembered or un-shipped.
 */
export function SyncStalenessBanner({ freshness }: { freshness: TaskSyncFreshness | undefined }) {
  // Translated with the quick-start (SUP-PROD-10): the page tells Spanish-speaking reviewers what this banner
  // means, so the banner itself must speak their language. The date follows the UI language too.
  const { t } = useTranslation();
  // Nothing loaded yet: say nothing. A banner that flashes on every page load gets ignored, and an
  // ignored warning is worse than none — this is the only thing the interim cannot afford.
  if (!freshness) return null;

  // The healthy case, and the one that retires this component.
  if (!freshness.isStale) return null;

  const days = Math.floor(freshness.ageHours / 24);
  const syncedOn = freshness.lastSyncedAt ? formatCalendarDate(freshness.lastSyncedAt) : null;

  return (
    <MessageBar intent="warning" role="status" style={{ marginBottom: 12 }}>
      <MessageBarBody>
        <MessageBarTitle>
          {syncedOn ? t('syncBanner.lastSynced', { date: syncedOn }) : t('syncBanner.unknown')}
        </MessageBarTitle>{' '}
        {syncedOn ? t('syncBanner.behind', { count: days }) : t('syncBanner.unknownBody')}
      </MessageBarBody>
    </MessageBar>
  );
}
